import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import UserCreateDrawer from '../components/UserCreateDrawer';
import UserDetailDrawer from '../components/drawers/UserDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Search, Download, UserPlus, ChevronLeft, ChevronRight, Gem, Star, Filter } from 'lucide-react';
const Users = () => {
  const location = useLocation();
  const {
    users
  } = useAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const usersPerPage = 10;
  // Check if we should open the drawer from navigation state
  useEffect(() => {
    if (location.state?.openDrawer) {
      setShowCreateDrawer(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  // Get unique cities for filter
  const uniqueCities = useMemo(() => {
    const cities = users.map(u => u.luogo).filter(Boolean);
    return ['all', ...Array.from(new Set(cities))];
  }, [users]);
  // Filter users with useMemo
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = user.nome.toLowerCase().includes(searchTerm.toLowerCase()) || user.cognome.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      const matchesCity = cityFilter === 'all' || user.luogo === cityFilter;
      return matchesSearch && matchesStatus && matchesCity;
    });
  }, [users, searchTerm, statusFilter, cityFilter]);
  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const handleExport = () => {
    console.log('Export CSV');
  };
  const handleAddUser = () => {
    setShowCreateDrawer(true);
  };
  const handleRowClick = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => setSelectedUserId(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-4134">
      <div className="w-20 flex-shrink-0" data-id="element-4135">
        <Sidebar onAddUser={handleAddUser} data-id="element-4136" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-4137">
        <Topbar data-id="element-4138" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-4139">
          <PageHeader title="Utenti" subtitle="Gestisci tutti gli utenti della piattaforma Greenback" primaryAction={{
          label: 'Aggiungi Utente',
          onClick: handleAddUser,
          icon: <UserPlus className="h-4 w-4 mr-2" data-id="element-4141" />
        }} secondaryAction={{
          label: 'Esporta CSV',
          onClick: handleExport,
          icon: <Download className="h-4 w-4 mr-2" data-id="element-4142" />
        }} data-id="element-4140" />

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-4143">
            <div className="relative flex-1 min-w-[300px]" data-id="element-4144">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-4145" />
              <input type="text" placeholder="Cerca per nome, cognome o email..." className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4146" />
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-4147">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-4148" />
              <select className="bg-transparent text-sm focus:outline-none" value={statusFilter} onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4149">
                <option value="all" data-id="element-4150">Tutti gli stati</option>
                <option value="active" data-id="element-4151">Attivo</option>
                <option value="inactive" data-id="element-4152">Inattivo</option>
                <option value="suspended" data-id="element-4153">Sospeso</option>
              </select>
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-4154">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-4155" />
              <select className="bg-transparent text-sm focus:outline-none" value={cityFilter} onChange={e => {
              setCityFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4156">
                <option value="all" data-id="element-4157">Tutte le città</option>
                {uniqueCities.filter(city => city !== 'all').map(city => <option key={city} value={city} data-id="element-4158">
                      {city}
                    </option>)}
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-4159">
            <div className="overflow-x-auto" data-id="element-4160">
              <table className="w-full" data-id="element-4161">
                <thead data-id="element-4162">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4163">
                    <th className="px-6 py-3" data-id="element-4164">Utente</th>
                    <th className="px-6 py-3" data-id="element-4165">Email</th>
                    <th className="px-6 py-3" data-id="element-4166">Luogo</th>
                    <th className="px-6 py-3" data-id="element-4167">Obiettivi</th>
                    <th className="px-6 py-3" data-id="element-4168">
                      <div className="flex items-center gap-1" data-id="element-4169">
                        <Gem className="h-3 w-3" data-id="element-4170" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4171">
                      <div className="flex items-center gap-1" data-id="element-4172">
                        <Gem className="h-3 w-3" data-id="element-4173" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4174">
                      <div className="flex items-center gap-1" data-id="element-4175">
                        <Star className="h-3 w-3" data-id="element-4176" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4177">
                      <div className="flex items-center gap-1" data-id="element-4178">
                        <Star className="h-3 w-3" data-id="element-4179" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4180">Riscatti</th>
                    <th className="px-6 py-3 text-right" data-id="element-4181">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-4182">
                  {currentUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(user.id)} data-id="element-4183">
                      <td className="px-6 py-4" data-id="element-4184">
                        <div className="flex items-center" data-id="element-4185">
                          <img src={user.avatar} alt={`${user.nome} ${user.cognome}`} className="h-10 w-10 rounded-full object-cover mr-3" data-id="element-4186" />
                          <div data-id="element-4187">
                            <div className="font-medium text-gray-900" data-id="element-4188">
                              {user.nome} {user.cognome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-4189">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-4190">
                        {user.luogo}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-4191">
                        {user.obiettivi}
                      </td>
                      <td className="px-6 py-4 font-medium text-blue-600" data-id="element-4192">
                        {user.creditiDiamanteOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-4193">
                        {user.creditiDiamanteSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-amber-600" data-id="element-4194">
                        {user.creditiStellaOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-4195">
                        {user.creditiStellaSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-4196">
                        {user.claimFatti}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-4197">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRowClick(user.id);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-4198">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-4199">
              <div className="text-sm text-gray-500" data-id="element-4200">
                Mostrando {indexOfFirstUser + 1} -{' '}
                {Math.min(indexOfLastUser, filteredUsers.length)} di{' '}
                {filteredUsers.length} utenti
              </div>
              <div className="flex items-center gap-2" data-id="element-4201">
                <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4202">
                  <ChevronLeft className="h-4 w-4" data-id="element-4203" />
                </button>
                <span className="text-sm text-gray-700" data-id="element-4204">
                  Pagina {currentPage} di {totalPages}
                </span>
                <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4205">
                  <ChevronRight className="h-4 w-4" data-id="element-4206" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* User Create Drawer */}
      <UserCreateDrawer isOpen={showCreateDrawer} onClose={() => setShowCreateDrawer(false)} onSuccess={() => {
      console.log('User created successfully');
    }} data-id="element-4207" />

      {/* User Detail Drawer */}
      <UserDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} userId={selectedUserId} data-id="element-4208" />
    </div>;
};
export default Users;